import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { AiSurface } from '@/components/ui/ai-surface';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { usePulseListQuery } from '@/lib/api/pulse';
import { formatDateTime } from '@/lib/utils/format';

/**
 * The latest published contract pulse, on the engagement Overview.
 *
 * The Overview used to be tiles and counts only; the written read of where
 * the contract stands lived on Contract Compliance → Pulse reports. This
 * card brings the newest published narrative (AI-drafted, cited, published
 * by a person) to the front page, with its posture and date, and links to
 * the full report. With nothing published yet it says so and points at
 * where to draft one.
 */
const POSTURE: Record<string, PostureStatus> = {
  red: 'critical',
  yellow: 'at-risk',
  green: 'healthy',
};

/** Long narratives are clipped on the Overview; the full text is one click away. */
const EXCERPT_CHARS = 700;

export function LatestPulseCard({ engagementId }: { engagementId: string }) {
  const pulses = usePulseListQuery(engagementId, { status: 'published', limit: 1 });
  const latest = pulses.data?.items[0] ?? null;
  const narrative = latest?.narrative_text?.trim() ?? '';
  const clipped = narrative.length > EXCERPT_CHARS;

  return (
    <AiSurface
      as="section"
      className="rounded-card p-card-pad"
      aria-labelledby="latest-pulse-title"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="latest-pulse-title" className="t-h3">
          Latest contract pulse
        </h2>
        {latest ? (
          <div className="flex items-center gap-2">
            {latest.overall_posture ? (
              <PostureChip status={POSTURE[latest.overall_posture] ?? 'unassessed'} />
            ) : null}
            <span className="t-meta">
              {latest.cycle_label}
              {latest.published_at ? ` · published ${formatDateTime(latest.published_at)}` : ''}
            </span>
          </div>
        ) : null}
      </div>
      {pulses.isLoading ? (
        <p className="mt-2 text-body-sm text-ink-500">Loading…</p>
      ) : latest && narrative.length > 0 ? (
        <p className="mt-2 whitespace-pre-line text-body-sm leading-relaxed text-ink-700">
          {clipped ? `${narrative.slice(0, EXCERPT_CHARS).trimEnd()}…` : narrative}
        </p>
      ) : (
        <p className="mt-2 text-body-sm text-ink-500">
          No pulse report has been published yet. A pulse is the cited, written read of contract
          compliance for a reporting period.
        </p>
      )}
      <Link
        to="/engagements/$engagementId/contract/pulse"
        params={{ engagementId }}
        className="mt-3 inline-flex items-center gap-1 text-body-sm font-medium text-ai-ink underline decoration-ai-ink/40 underline-offset-2 hover:decoration-ai-ink"
      >
        {latest ? 'Open the full pulse report' : 'Draft a pulse'}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </AiSurface>
  );
}
